// supabase-js data helpers shared by the Worker routes.
// Pure (map-only) — no @supabase/supabase-js runtime dependency here.
import { columnMaps, type TableName } from './column-maps.generated';

export { columnMaps, type TableName };

/**
 * PostgREST `select` string that aliases every DB column back to its camelCase
 * key, so query results keep the exact shape the old Drizzle layer returned
 * (preserving the mobile/web API contract). e.g. "id:id,tenantId:tenant_id,…".
 */
export function selectAlias(table: TableName): string {
	const map = columnMaps[table] as Record<string, string>;
	return Object.entries(map)
		.map(([camel, col]) => `${camel}:${col}`)
		.join(',');
}

/**
 * Translate a camelCase write payload into a DB row keyed by snake_case
 * columns. Keys not present in the table map are dropped.
 */
export function toRow(table: TableName, input: Record<string, unknown>): Record<string, unknown> {
	const map = columnMaps[table] as Record<string, string>;
	const row: Record<string, unknown> = {};
	for (const [camel, value] of Object.entries(input)) {
		const col = map[camel];
		if (col) row[col] = value;
	}
	return row;
}

/**
 * Inverse of {@link toRow}: translate a raw snake_case DB row (e.g. a Supabase
 * Realtime `payload.new`) back into the camelCase shape the API returns via
 * {@link selectAlias}, so realtime updates match REST responses. Columns not in
 * the table map are dropped.
 */
export function fromRow(table: TableName, row: Record<string, unknown>): Record<string, unknown> {
	const map = columnMaps[table] as Record<string, string>;
	const out: Record<string, unknown> = {};
	for (const [camel, col] of Object.entries(map)) {
		if (col in row) out[camel] = row[col];
	}
	return out;
}

export {
	configurationTotalsFromRow,
	type ConfigurationTotals,
	type ConfigurationTotalsRow,
} from './configuration-totals';
export { DEFAULT_CER_FEATURES, parseCerFeatures, type CerFeatures } from './cer-features';
export { demoSuggestions } from './cer-features';
export { plainDashes, withPlainDashes } from './plain-dashes';
